import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faDollarSign,
  faChartLine,
  faGlobe,
  faDatabase,
  faMicrochip,
  faHashtag,
} from "@fortawesome/free-solid-svg-icons";
import { detectCsvDataType, DATA_TYPE_LABELS } from "../../../utils/csvDataType";
import RawFallback from "../../Shared/RawFallback/RawFallback";
import styles from "./CSVOverview.module.css";

const CSVOverview = ({ data, rawText, analysisData }) => {
  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>No overview data available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  const dataType = detectCsvDataType(analysisData?.columns);
  const dataTypeLabel = DATA_TYPE_LABELS[dataType] || "Generic";

  const computeOverviewCards = () => {
    const rows = analysisData?.data || [];
    const cols = analysisData?.columns || [];
    const cards = [];
    if (rows.length > 0) {
      cards.push({ label: "Rows", value: (analysisData?.metadata?.totalRows || rows.length).toLocaleString(), icon: faHashtag });
      cards.push({ label: "Columns", value: String(cols.length), icon: faDatabase });
    }
    if (dataType === "sales" && cols.length > 0) {
      const revIdx = cols.findIndex((c) => /revenue/i.test(c));
      const qtyIdx = cols.findIndex((c) => /quantity/i.test(c));
      const regionIdx = cols.findIndex((c) => /region/i.test(c));
      const productIdx = cols.findIndex((c) => /product/i.test(c));
      if (revIdx >= 0) {
        const revs = rows.map((r) => parseFloat((Array.isArray(r) ? r[revIdx] : r[cols[revIdx]] || 0).toString().replace(/[^0-9.-]/g, "")) || 0);
        const total = revs.reduce((a, b) => a + b, 0);
        if (total > 0) {
          cards.push({ label: "Total Revenue", value: total, icon: faDollarSign, format: "currency" });
          cards.push({ label: "Avg Order Value", value: total / rows.length, icon: faChartLine, format: "currency" });
        }
      }
      if (regionIdx >= 0) {
        const regions = new Set(rows.map((r) => (Array.isArray(r) ? r[regionIdx] : r[cols[regionIdx]])).filter(Boolean));
        if (regions.size > 0) cards.push({ label: "Regions", value: String(regions.size), icon: faGlobe });
      }
      if (productIdx >= 0) {
        const products = new Set(rows.map((r) => (Array.isArray(r) ? r[productIdx] : r[cols[productIdx]])).filter(Boolean));
        if (products.size > 0) cards.push({ label: "Products", value: String(products.size), icon: faShoppingCart });
      }
    }
    if (dataType === "ml_scientific" && cols.length > 0) {
      const epochIdx = cols.findIndex((c) => /^epoch$/i.test(c));
      if (epochIdx >= 0) {
        const epochs = rows.map((r) => parseFloat((Array.isArray(r) ? r[epochIdx] : r[cols[epochIdx]] || 0).toString().replace(/[^0-9.-]/g, ""))).filter((n) => !isNaN(n));
        if (epochs.length > 0) {
          const minE = Math.min(...epochs);
          const maxE = Math.max(...epochs);
          cards.push({ label: "Epoch Range", value: `${minE} – ${maxE}`, icon: faMicrochip });
        }
      }
    }
    return cards;
  };

  const iconForLabel = (label) => {
    const l = (label || "").toLowerCase();
    if (l.includes("revenue") || l.includes("price")) return faDollarSign;
    if (l.includes("region") || l.includes("product")) return faGlobe;
    if (l.includes("epoch") || l.includes("loss") || l.includes("metric")) return faMicrochip;
    if (l.includes("row") || l.includes("record") || l.includes("transaction") || l.includes("count")) return faHashtag;
    return faChartLine;
  };

  const ML_SKIP_KEYS = new Set(["file type", "rows", "columns", "headers", "column names", "data type", "contains missing values"]);
  const extractDynamicKPIs = () => {
    const items = [];
    if (data?.sections) {
      const metricSections = ["document overview", "key insights", "key metrics", "summary insights"];
      for (const section of data.sections) {
        const name = (section.name || "").toLowerCase();
        if (!metricSections.some((m) => name.includes(m))) continue;
        for (const item of section.content || []) {
          if (item.type === "keyValue" && item.key?.trim() && item.value?.trim()) {
            const keyLower = item.key.trim().toLowerCase();
            if (dataType === "ml_scientific" && ML_SKIP_KEYS.has(keyLower)) continue;
            const val = String(item.value).trim();
            if (val && val !== "N/A" && !val.includes("N/A") && val.length < 80) {
              items.push({ label: item.key.trim(), value: val });
            }
          }
        }
      }
    }
    if (items.length === 0 && rawText) {
      const lines = rawText.split(/\n/);
      const kvPattern = /^([A-Za-z][^:]{2,50}):\s*(.+)$/;
      for (const line of lines) {
        const m = line.trim().match(kvPattern);
        if (m) {
          const val = m[2].trim();
          if (val && val !== "N/A" && val.length < 80) {
            items.push({ label: m[1].trim(), value: val });
          }
        }
      }
    }
    const seen = new Set();
    return items
      .filter((i) => {
        const k = i.label.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, 6);
  };

  const extractKPIs = () => {
    const kpis = {
      totalTransactions: null,
      totalRevenue: null,
      avgRevenue: null,
      topRegion: null,
    };

    if (!data || !data.sections) return kpis;

    data.sections.forEach((section) => {
      if (section.tables && section.tables.length > 0) {
        section.tables.forEach((table) => {
          if (table.rows) {
            table.rows.forEach((row) => {
              if (row.length >= 2) {
                const key = (row[0] || "").toLowerCase();
                const value = row[1] || row[2] || "";

                if ((key.includes("total transactions") || key.includes("rows")) && !kpis.totalTransactions) {
                  kpis.totalTransactions = value;
                }
                if (key.includes("total revenue") && !kpis.totalRevenue) {
                  kpis.totalRevenue = value;
                }
                if ((key.includes("avg revenue") || key.includes("average revenue") || key.includes("average revenue per transaction")) && !kpis.avgRevenue) {
                  kpis.avgRevenue = value;
                }
                if ((key.includes("most frequent region") || key.includes("top region")) && !kpis.topRegion) {
                  kpis.topRegion = value;
                }
              }
            });
          }
        });
      }

      if (section.content) {
        section.content.forEach((item) => {
          if (item.type === 'keyValue' && item.key && item.value) {
            const key = item.key.toLowerCase();
            const value = item.value;
            
            if ((key.includes("total transactions") || key.includes("rows")) && !kpis.totalTransactions) {
              kpis.totalTransactions = value;
            }
            if (key.includes("total revenue") && !kpis.totalRevenue) {
              kpis.totalRevenue = value;
            }
            if ((key.includes("avg revenue") || key.includes("average revenue") || key.includes("average revenue per transaction")) && !kpis.avgRevenue) {
              kpis.avgRevenue = value;
            }
            if ((key.includes("most frequent region") || key.includes("top region")) && !kpis.topRegion) {
              kpis.topRegion = value.split('(')[0].trim();
            }
          } else {
            const text = item.text || "";
            const lowerText = text.toLowerCase();
            
            if (lowerText.includes("total transactions") && !kpis.totalTransactions) {
              const match = text.match(/total\s+transactions[:-]?\s*(\d+[\d,]*)/i);
              if (match) {
                kpis.totalTransactions = match[1];
              } else {
                const numMatch = text.match(/(\d+[\d,]*)\s*transactions?/i);
                if (numMatch) kpis.totalTransactions = numMatch[1];
              }
            }
            
            if (lowerText.includes("total revenue") && !kpis.totalRevenue) {
              const match = text.match(/total\s+revenue[:-]?\s*\$?([\d,]+\.?\d*)/i);
              if (match) {
                kpis.totalRevenue = match[1];
              }
            }
            
            if ((lowerText.includes("avg revenue") || lowerText.includes("average revenue") || lowerText.includes("average revenue per transaction") || lowerText.includes("revenue per transaction")) && !kpis.avgRevenue) {
              let match = text.match(/average\s+revenue\s+per\s+transaction[:-]?\s*\$?([\d,]+\.?\d*)/i);
              if (match) {
                kpis.avgRevenue = match[1];
              } else {
                match = text.match(/(?:avg|average)\s+revenue[:-]?\s*\$?([\d,]+\.?\d*)/i);
                if (match) {
                  kpis.avgRevenue = match[1];
                } else {
                  const altMatch = text.match(/revenue\s+per\s+transaction[:-]?\s*\$?([\d,]+\.?\d*)/i);
                  if (altMatch) kpis.avgRevenue = altMatch[1];
                }
              }
            }
            
            if ((lowerText.includes("most frequent region") || lowerText.includes("top region")) && !kpis.topRegion) {
              const match = text.match(/(?:most\s+frequent|top)\s+region[:-]?\s*([^(\n]+)/i);
              if (match) {
                kpis.topRegion = match[1].trim().split('(')[0].trim();
              }
            }
          }
        });
      }
    });

    if (rawText && (!kpis.totalTransactions || !kpis.totalRevenue)) {
      if (!kpis.totalTransactions) {
        const match = rawText.match(/total\s+transactions[:-]?\s*(\d+[\d,]*)/i);
        if (match) kpis.totalTransactions = match[1];
      }
      
      if (!kpis.totalRevenue) {
        const match = rawText.match(/total\s+revenue[:-]?\s*\$?([\d,]+\.?\d*)/i);
        if (match) kpis.totalRevenue = match[1];
      }
      
      if (!kpis.avgRevenue) {
        let match = rawText.match(/average\s+revenue\s+per\s+transaction[:-]?\s*\$?([\d,]+\.?\d*)/i);
        if (match) {
          kpis.avgRevenue = match[1];
        } else {
          match = rawText.match(/(?:avg|average)\s+revenue[:-]?\s*\$?([\d,]+\.?\d*)/i);
          if (match) {
            kpis.avgRevenue = match[1];
          } else {
            const altMatch = rawText.match(/revenue\s+per\s+transaction[:-]?\s*\$?([\d,]+\.?\d*)/i);
            if (altMatch) kpis.avgRevenue = altMatch[1];
          }
        }
      }
      
      if (!kpis.topRegion) {
        const match = rawText.match(/(?:most\s+frequent|top)\s+region[:-]?\s*([^(\n]+)/i);
        if (match) {
          kpis.topRegion = match[1].trim().split('(')[0].trim();
        }
      }
    }

    return kpis;
  };

  const extractChartData = () => {
    const charts = {
      revenueByRegion: [],
      topProducts: [],
    };

    if (data.sections) {
      data.sections.forEach((section) => {
        const sectionName = section.name?.toLowerCase() || "";
        
        if (sectionName.includes("regional") && section.tables) {
          section.tables.forEach((table) => {
            if (table.headers && table.headers.length >= 2) {
              const regionIndex = table.headers.findIndex(h => 
                h.toLowerCase().includes("region")
              );
              let revenueIndex = table.headers.findIndex(h => 
                h.toLowerCase().includes("total revenue")
              );
              if (revenueIndex < 0) {
                revenueIndex = table.headers.findIndex(h => 
                  h.toLowerCase().includes("revenue")
                );
              }

              if (regionIndex >= 0 && revenueIndex >= 0 && table.rows) {
                table.rows.forEach((row) => {
                  if (row[regionIndex] && row[revenueIndex]) {
                    const revenueStr = row[revenueIndex].toString();
                    const revenue = parseFloat(revenueStr.replace(/[^0-9.]/g, "")) || 0;
                    if (revenue > 0) {
                      charts.revenueByRegion.push({
                        region: row[regionIndex],
                        revenue: revenue,
                      });
                    }
                  }
                });
              }
            }
          });
        }

        if (sectionName.includes("product") && section.tables) {
          section.tables.forEach((table) => {
            if (table.headers && table.rows) {
              const productIndex = table.headers.findIndex(h => 
                h.toLowerCase().includes("product")
              );
              const revenueIndex = table.headers.findIndex(h => 
                h.toLowerCase().includes("revenue")
              );

              if (productIndex >= 0 && revenueIndex >= 0) {
                table.rows.slice(0, 5).forEach((row) => {
                  if (row[productIndex] && row[revenueIndex]) {
                    charts.topProducts.push({
                      product: row[productIndex],
                      revenue: parseFloat(row[revenueIndex].replace(/[^0-9.]/g, "")) || 0,
                    });
                  }
                });
              }
            }
          });
        }
      });
    }

    return charts;
  };

  const kpis = extractKPIs();
  const dynamicKPIs = extractDynamicKPIs();
  const chartData = extractChartData();

  const formatCurrency = (value) => {
    if (!value || value === "N/A") return "N/A";
    const str = value.toString();
    const num = parseFloat(str.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) {
      if (str.includes("$")) return str;
      return value;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatNumber = (value) => {
    if (!value) return "N/A";
    const num = parseFloat(value.toString().replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return value;
    return new Intl.NumberFormat("en-US").format(num);
  };

  const totalRevenueByRegion = chartData.revenueByRegion.reduce(
    (sum, item) => sum + item.revenue,
    0
  );

  const maxProductRevenue = chartData.topProducts.length > 0
    ? Math.max(...chartData.topProducts.map(p => p.revenue))
    : 0;


  const renderAtAGlance = () => {
    const rows = analysisData?.metadata?.totalRows || analysisData?.data?.length || 0;
    const cols = analysisData?.columns?.length || 0;
    if (!rows && !cols) return null;
    return (
      <div className={styles.atAGlanceCard}>
        <h3 className={styles.atAGlanceTitle}>At a Glance</h3>
        <div className={styles.atAGlanceGrid}>
          <div className={styles.atAGlanceItem}>
            <span className={styles.atAGlanceLabel}>File Type</span>
            <span className={styles.atAGlanceValue}>CSV</span>
          </div>
          <div className={styles.atAGlanceItem}>
            <span className={styles.atAGlanceLabel}>Rows</span>
            <span className={styles.atAGlanceValue}>{Number(rows).toLocaleString()}</span>
          </div>
          <div className={styles.atAGlanceItem}>
            <span className={styles.atAGlanceLabel}>Columns</span>
            <span className={styles.atAGlanceValue}>{cols}</span>
          </div>
          {analysisData?.metadata?.hasHeaders !== undefined && (
            <div className={styles.atAGlanceItem}>
              <span className={styles.atAGlanceLabel}>Headers</span>
              <span className={styles.atAGlanceValue}>{analysisData.metadata.hasHeaders ? "Yes" : "No"}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderKeyInsights = () => {
    const documentOverview = data.sections?.find(s =>
      s.name?.toLowerCase().includes("document overview")
    );
    
    const keyInsights = data.sections?.find(s => {
      const name = s.name?.toLowerCase() || '';
      return name === "key insights" || 
             name.includes("key insights") || 
             name.includes("summary insights") ||
             (name.includes("insights") && !name.includes("overview"));
    });

    const highlights = data.sections?.find(s => {
      const name = s.name?.toLowerCase() || '';
      return name === "patterns and trends" ||
             name === "patterns & trends" ||
             name.includes("patterns and trends") ||
             name.includes("patterns & trends") ||
             (name.includes("patterns") && name.includes("trends")) ||
             name.includes("highlights");
    });

    if (!documentOverview && !keyInsights && !highlights && !data.introText) {
      return null;
    }

    const renderContentItems = (content) => {
      if (!content || content.length === 0) return null;
      const items = content.map((item, idx) => {
        if (item.type === 'keyValue' && item.key && item.value) {
          return (
            <li key={idx} className={styles.insightItem}>
              <span className={styles.bulletPoint}>•</span>
              <span>
                <strong>{item.key}:</strong> {item.value}
              </span>
            </li>
          );
        } else if (item.type === 'bullet' && item.text) {
          return (
            <li key={idx} className={styles.insightItem}>
              <span className={styles.bulletPoint}>•</span>
              <span>{item.text}</span>
            </li>
          );
        } else if (item.type === 'subsection' && item.text) {
          const parts = item.text.split(':');
          if (parts.length >= 2 && parts[1].trim()) {
            return (
              <li key={idx} className={styles.insightItem}>
                <span className={styles.bulletPoint}>•</span>
                <span>
                  <strong>{parts[0].trim()}:</strong> {parts.slice(1).join(':').trim()}
                </span>
              </li>
            );
          } else {
            return (
              <li key={idx} className={styles.insightItem}>
                <span className={styles.bulletPoint}>•</span>
                <span><strong>{item.text}</strong></span>
              </li>
            );
          }
        } else if (item.type === 'text' && item.text) {
          if (item.text.includes(':')) {
            const parts = item.text.split(':');
            if (parts.length >= 2 && parts[1].trim()) {
              return (
                <li key={idx} className={styles.insightItem}>
                  <span className={styles.bulletPoint}>•</span>
                  <span>
                    <strong>{parts[0].trim()}:</strong> {parts.slice(1).join(':').trim()}
                  </span>
                </li>
              );
            }
          }
          if (item.text.trim().length > 0) {
            return (
              <li key={idx} className={styles.insightItem}>
                <span className={styles.bulletPoint}>•</span>
                <span>{item.text}</span>
              </li>
            );
          }
        }
        return null;
      }).filter(item => item !== null);
      if (items.length === 0) return null;
      return items;
    };

    const docOverviewContent = documentOverview?.content?.length
      ? (dataType === "ml_scientific"
          ? documentOverview.content.filter(
              (item) =>
                item.type !== "keyValue" ||
                !ML_SKIP_KEYS.has((item.key || "").toLowerCase())
            )
          : documentOverview.content)
      : [];

    return (
      <div className={styles.keyInsightsSection}>
        {documentOverview && docOverviewContent.length > 0 && (
          <div className={styles.insightCard}>
            <h3 className={styles.insightCardTitle}>{dataType === "ml_scientific" ? "Overview" : "Document Overview"}</h3>
            <ul className={styles.insightsList}>
              {renderContentItems(docOverviewContent)}
            </ul>
          </div>
        )}

        {keyInsights && (
          <div className={styles.insightCard}>
            <h3 className={styles.insightCardTitle}>Summary Insights</h3>
            {keyInsights.content && keyInsights.content.length > 0 ? (
              <ul className={styles.insightsList}>
                {renderContentItems(keyInsights.content)}
              </ul>
            ) : (
              <p className={styles.summaryText}>No insights data available</p>
            )}
          </div>
        )}

        {highlights && (
          <div className={styles.insightCard}>
            <h3 className={styles.insightCardTitle}>Quick Highlights</h3>
            {highlights.content && highlights.content.length > 0 ? (
              <ul className={styles.insightsList}>
                {renderContentItems(highlights.content)}
              </ul>
            ) : (
              <p className={styles.summaryText}>No highlights data available</p>
            )}
          </div>
        )}

        {!documentOverview && !keyInsights && !highlights && data.introText && (
          <div className={styles.insightCard}>
            <h3 className={styles.insightCardTitle}>Key Insights</h3>
            <p className={styles.summaryText}>{data.introText}</p>
          </div>
        )}
      </div>
    );
  };

  const computedCardsRaw = computeOverviewCards();
  const computedCards = dataType === "ml_scientific"
    ? computedCardsRaw.filter((c) => c.label !== "Rows" && c.label !== "Columns")
    : computedCardsRaw;
  const legacyKpiCards = [
    { label: "Total Transactions", value: kpis.totalTransactions, format: "number", icon: faShoppingCart },
    { label: "Total Revenue", value: kpis.totalRevenue, format: "currency", icon: faDollarSign },
    { label: "Avg Revenue", value: kpis.avgRevenue, format: "currency", icon: faChartLine },
    { label: "Top Region", value: kpis.topRegion, format: "text", icon: faGlobe },
  ].filter((c) => c.value && String(c.value) !== "N/A");

  const fromComputed = computedCards.map((c) => ({
    label: c.label,
    value: c.format === "currency" ? c.value : String(c.value),
    format: c.format || "text",
    icon: c.icon,
  }));

  const fromDynamic = dynamicKPIs
    .filter((k) => !fromComputed.some((c) => c.label.toLowerCase() === k.label.toLowerCase()))
    .map((k) => ({ label: k.label, value: k.value, format: "text", icon: iconForLabel(k.label) }));

  const fromLegacy = legacyKpiCards.filter(
    (c) => !fromComputed.some((x) => x.label.toLowerCase().includes(c.label.toLowerCase().split(" ")[0]))
  );

  const kpiCards = fromComputed.length > 0
    ? [...fromComputed, ...fromDynamic.slice(0, 4)]
    : dynamicKPIs.length > 0
      ? dynamicKPIs.map((k) => ({ label: k.label, value: k.value, format: "text", icon: iconForLabel(k.label) }))
      : legacyKpiCards;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faDatabase} />
        </div>
        <div className={styles.headerText}>
          <div className={styles.headerTitleRow}>
            <h2 className={styles.title}>Overview</h2>
            <span className={styles.dataTypeBadge}>{dataTypeLabel}</span>
          </div>
        </div>
      </div>

      {renderAtAGlance()}

      {kpiCards.length > 0 && (
        <div className={styles.kpiGrid}>
          {kpiCards.map((card, idx) => (
            <div key={idx} className={styles.kpiCard}>
              <div className={styles.kpiIcon}>
                <FontAwesomeIcon icon={card.icon} />
              </div>
              <div className={styles.kpiContent}>
                <div className={styles.kpiLabel}>{card.label}</div>
                <div className={styles.kpiValue}>
                  {card.format === "currency"
                    ? formatCurrency(card.value)
                    : card.format === "number"
                    ? formatNumber(card.value)
                    : card.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.chartsGrid}>
        {chartData.revenueByRegion.length > 0 && (
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Revenue by Region</h3>
            <div className={styles.pieChart}>
              {chartData.revenueByRegion.map((item, idx) => {
                const percentage = totalRevenueByRegion > 0
                  ? (item.revenue / totalRevenueByRegion) * 100
                  : 0;
                return (
                  <div key={idx} className={styles.pieSegment}>
                    <div className={styles.pieSegmentBar}>
                      <div
                        className={styles.pieSegmentFill}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className={styles.pieSegmentLabel}>
                      <span className={styles.pieSegmentName}>{item.region}</span>
                      <span className={styles.pieSegmentValue}>
                        {formatCurrency(item.revenue)} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {chartData.topProducts.length > 0 && (
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Top Products</h3>
            <div className={styles.barChart}>
              {chartData.topProducts.map((item, idx) => {
                const percentage = maxProductRevenue > 0
                  ? (item.revenue / maxProductRevenue) * 100
                  : 0;
                return (
                  <div key={idx} className={styles.barItem}>
                    <div className={styles.barLabel}>{item.product}</div>
                    <div className={styles.barContainer}>
                      <div
                        className={styles.barFill}
                        style={{ width: `${percentage}%` }}
                      />
                      <span className={styles.barValue}>
                        {formatCurrency(item.revenue)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {renderKeyInsights()}
      {kpiCards.length === 0 && !renderKeyInsights() && rawText && (
        <RawFallback rawText={rawText} accentColor="#22c55e" />
      )}
    </div>
  );
};

export default CSVOverview;


